/**
 * v1 User Detail Page
 * Comprehensive user detail view with tabbed interface
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { v1UsersApi, User } from '@/features/users/api'
import { PageHeader, PageContent, DetailTabs } from '@/components/shared/PageLayout'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { Loader2, AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react'
import { UserOverviewTab } from '@/features/users/components/UserDetailTabs/UserOverviewTab'
import { UserBalanceTab } from '@/features/users/components/UserDetailTabs/UserBalanceTab'
import { UserAgentsTab } from '@/features/users/components/UserDetailTabs/UserAgentsTab'
import { UserActivityTab } from '@/features/users/components/UserDetailTabs/UserActivityTab'

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')

  const {
    data: user,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery<User, Error>({
    queryKey: ['user', userId],
    queryFn: () => v1UsersApi.getUserById(userId!),
    enabled: !!userId,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading user details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <div className="ml-2">
            <h4 className="font-medium">Error loading user</h4>
            <p className="text-sm mt-1">{error.message}</p>
          </div>
        </Alert>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <div className="ml-2">
            <h4 className="font-medium">User not found</h4>
            <p className="text-sm mt-1">The user you're looking for doesn't exist.</p>
          </div>
        </Alert>
      </div>
    )
  }

  const tabs = [
    {
      value: 'overview',
      label: 'Overview',
      icon: 'user',
    },
    {
      value: 'balance',
      label: 'Balance & Credits',
      icon: 'coins',
    },
    {
      value: 'agents',
      label: 'Agents',
      icon: 'bot',
      badge: user.total_agents,
    },
    {
      value: 'activity',
      label: 'Activity',
      icon: 'activity',
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={user.name || user.username || user.email}
        description={`User ID: ${user._id}`}
        breadcrumbs={[
          { label: 'Users', href: '/users' },
          { label: user.name || user.email },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/users')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Users
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>
        }
      />

      <PageContent>
        <DetailTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <div className="mt-6">
          {activeTab === 'overview' && <UserOverviewTab user={user} />}
          {activeTab === 'balance' && <UserBalanceTab user={user} onRefresh={refetch} />}
          {activeTab === 'agents' && <UserAgentsTab user={user} />}
          {activeTab === 'activity' && <UserActivityTab user={user} />}
        </div>
      </PageContent>
    </div>
  )
}
